import { useEffect, useMemo, useState } from "react";
import { BASE_API_URL } from "../../Components/config";
import { groupByAssignment } from "../marking/markingTokens";

const WEEK = 7 * 86400000;

/**
 * The work set in a space, summed up for its home page: how much is waiting
 * to be marked, what is due this week, and the few assignments most in
 * need of attention. The full view (MarkingPanel) reads the same rows, so
 * the two can never disagree about a number.
 */
export function useSpaceWork({ courseGuid, isInstructor }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!courseGuid) return undefined;
    let live = true;
    fetch(
      `${BASE_API_URL}/api/v1/user-course-links/user-paper-attempts/detail/?course_user_type=${
        isInstructor ? "INSTRUCTOR" : "STUDENT"
      }&course_guid=${courseGuid}&limit=200`,
      { headers: { Authorization: `Bearer ${localStorage.getItem("accessToken")}` } }
    )
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`load failed (${res.status})`))))
      .then((data) => live && (setRows(Array.isArray(data) ? data : []), setError(null)))
      .catch((e) => {
        if (!live) return;
        console.error(e);
        setRows([]);
        setError("Couldn't load the work for this space.");
      });
    return () => {
      live = false;
    };
  }, [courseGuid, isInstructor]);

  return useMemo(() => {
    const assignments = groupByAssignment(rows || []);
    const now = Date.now();
    const dueThisWeek = assignments.filter((a) => {
      const due = a.dueDate ? new Date(a.dueDate).getTime() : null;
      return due != null && due >= now - 86400000 && due <= now + WEEK;
    });
    return {
      isLoading: rows === null,
      error,
      assignments,
      toMark: assignments.reduce((n, a) => n + a.needsReview, 0),
      dueThisWeek: dueThisWeek.length,
      // A student's own outstanding work: set but not handed in.
      toDo: (rows || []).filter((r) => r.submission_status !== "SUBMITTED").length,
      top: assignments.slice(0, 3),
    };
  }, [rows, error]);
}
